import React, { useEffect, useRef, useState } from 'react';
import { Canvas, Textbox, Image as FabricImage, Line, Rect } from 'fabric';

const ProductEditor = () => {
   const canvasRef = useRef(null);
   const [fabricCanvas, setFabricCanvas] = useState(null);
   const [textObject, setTextObject] = useState(null);
   const [imageObject, setImageObject] = useState(null);
   const [formData, setFormData] = useState({
      text: 'Default Text',
      image: null,
   });

   useEffect(() => {
      const canvas = new Canvas(canvasRef.current, {
         width: 800,
         height: 800,
         backgroundColor: '#f3f3f3',
      });

      setFabricCanvas(canvas);
      drawRulers(canvas); // Call to draw the rulers

      // Add default image and text after initializing the canvas
      addDefaultImage();
      addText(formData.text);

      return () => {
         if (canvas) {
            canvas.dispose();
         }
      };
   }, []);

   const drawRulers = (canvas) => {
      const rulerHeight = 20; // Height of the ruler
      const rulerWidth = canvas.width; // Width of the ruler

      // Create the horizontal ruler
      const horizontalRuler = new Rect({
         left: 0,
         top: 0,
         width: rulerWidth,
         height: rulerHeight,
         fill: '#ddd',
      });

      canvas.add(horizontalRuler);

      // Create vertical ruler
      const verticalRuler = new Rect({
         left: 0,
         top: 0,
         width: rulerHeight,
         height: canvas.height,
         fill: '#ddd',
      });

      canvas.add(verticalRuler);

      // Add ticks and labels to horizontal ruler
      for (let i = 0; i <= canvas.width; i += 50) {
         const tick = new Line([i, 0, i, rulerHeight], {
            stroke: '#000',
            strokeWidth: 1,
         });
         canvas.add(tick);
         const tickLabel = new Textbox(i.toString(), {
            left: i,
            top: 5,
            fontSize: 12,
            fill: '#000',
            editable: false,
         });
         canvas.add(tickLabel);
      }

      // Add ticks and labels to vertical ruler
      for (let i = 0; i <= canvas.height; i += 50) {
         const tick = new Line([0, i, rulerHeight, i], {
            stroke: '#000',
            strokeWidth: 1,
         });
         canvas.add(tick);
         const tickLabel = new Textbox(i.toString(), {
            left: 5,
            top: i,
            fontSize: 12,
            fill: '#000',
            editable: false,
         });
         canvas.add(tickLabel);
      }

      canvas.renderAll(); // Re-render canvas after adding rulers
   };

   const addText = (text) => {
      if (!fabricCanvas) return;

      const newText = new Textbox(text, {
         left: 150,
         top: 150,
         fontSize: 24,
         fill: '#000',
         editable: true,
      });
      fabricCanvas.add(newText);
      setTextObject(newText);
      fabricCanvas.setActiveObject(newText);
      fabricCanvas.renderAll();
   };

   const addDefaultImage = () => {
      const defaultImageURL = 'https://www.api.vareprint.com:3008/images/Twitter.png'; // Replace with a valid default image URL
      addImage(defaultImageURL);
   };

   const addImage = (imageURL) => {
      if (!fabricCanvas || !imageURL) return;

      const imgObj = new window.Image();
      imgObj.src = imageURL;
      imgObj.onload = function () {
         const img = new FabricImage(imgObj, {
            left: 100,
            top: 100,
            scaleX: 0.5,
            scaleY: 0.5,
         });
         fabricCanvas.add(img);
         setImageObject(img);
         fabricCanvas.setActiveObject(img);
         fabricCanvas.renderAll();
      };

      // Optional: Handle image loading errors
      imgObj.onerror = () => {
         console.error('Failed to load image:', imageURL);
      };
   };

   const changeColor = (color) => {
      if (!fabricCanvas) return;

      const activeObject = fabricCanvas.getActiveObject();
      if (activeObject && activeObject.type === 'textbox') {
         activeObject.set({ fill: color });
         fabricCanvas.renderAll();
      }
   };

   const toggleBold = () => {
      if (!fabricCanvas) return;

      const activeObject = fabricCanvas.getActiveObject();
      if (activeObject && activeObject.type === 'textbox') {
         activeObject.set({ fontWeight: activeObject.fontWeight === 'bold' ? 'normal' : 'bold' });
         fabricCanvas.renderAll();
      }
   };

   const toggleItalic = () => {
      if (!fabricCanvas) return;

      const activeObject = fabricCanvas.getActiveObject();
      if (activeObject && activeObject.type === 'textbox') {
         activeObject.set({ fontStyle: activeObject.fontStyle === 'italic' ? 'normal' : 'italic' });
         fabricCanvas.renderAll();
      }
   };

   const toggleUnderline = () => {
      if (!fabricCanvas) return;

      const activeObject = fabricCanvas.getActiveObject();
      if (activeObject && activeObject.type === 'textbox') {
         activeObject.set({ underline: !activeObject.underline });
         fabricCanvas.renderAll();
      }
   };

   const deleteObject = () => {
      if (!fabricCanvas) return;

      const activeObject = fabricCanvas.getActiveObject();
      if (activeObject) {
         fabricCanvas.remove(activeObject);
         fabricCanvas.renderAll();
      }
   };

   const clearCanvas = () => {
      if (!fabricCanvas) return;

      fabricCanvas.clear();
      fabricCanvas.backgroundColor = '#f3f3f3';
      setTextObject(null);
      setImageObject(null);
      drawRulers(fabricCanvas); // Redraw the rulers after clearing
      fabricCanvas.renderAll();
   };

   const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
         ...prev,
         [name]: value,
      }));

      if (name === 'text' && textObject) {
         textObject.set({ text: value });
         fabricCanvas.renderAll();
      }
   };

   const handleSubmit = (e) => {
      e.preventDefault();
      if (!textObject) addText(formData.text);
      if (!imageObject && formData.image) addImage(formData.image);
   };

   return (
      <div>
         <div style={{ marginBottom: '20px' }}>
            <button onClick={() => addText('New Text')}>Add Text</button>
            <input type="file" accept="image/*" onChange={(e) => {
               const file = e.target.files[0];
               if (file) {
                  const imageURL = URL.createObjectURL(file);
                  setFormData((prev) => ({ ...prev, image: imageURL }));
                  addImage(imageURL);
               }
            }} style={{ marginLeft: '10px' }} />
            <input 
               type="color" 
               onChange={(e) => changeColor(e.target.value)} 
               style={{ marginLeft: '10px' }} 
               title="Change Color"
            />
            <button onClick={toggleBold} style={{ marginLeft: '10px' }}><strong>B</strong></button>
            <button onClick={toggleItalic} style={{ marginLeft: '10px' }}><em>I</em></button>
            <button onClick={toggleUnderline} style={{ marginLeft: '10px' }}><u>U</u></button>
            <button onClick={deleteObject} style={{ marginLeft: '10px' }}>Delete Object</button>
            <button onClick={clearCanvas} style={{ marginLeft: '10px' }}>Clear Canvas</button>
         </div>

         <div style={{ border: '1px solid #ccc', display: 'inline-block' }}>
            <canvas ref={canvasRef} />
         </div>

         <form onSubmit={handleSubmit} style={{ marginTop: '20px' }}>
            <h3>Product Details</h3>
            <div>
               <label>Text:</label>
               <input 
                  type="text" 
                  name="text" 
                  value={formData.text} 
                  onChange={handleInputChange} 
                  required 
               />
            </div>
            <div>
               <label>Image:</label>
               <input 
                  type="file" 
                  accept="image/*" 
                  onChange={(e) => {
                     const file = e.target.files[0];
                     if (file) {
                        const imageURL = URL.createObjectURL(file);
                        setFormData((prev) => ({ ...prev, image: imageURL }));
                     }
                  }} 
               />
            </div>
            <button type="submit">Add Product</button>
         </form>
      </div>
   );
};

export default ProductEditor;
